<!--
  MiniGraphVisualization — sparkline-styled preview of a real dataset
  visualization configuration.

  Used by `MyChartsSection` (Mijn omgeving snapshot + Overzicht) to render
  a favourite chart in a tiny card. We fetch the FULL configured
  visualization via `getVisualization` so the result honours:
  - every series in `series_bindings` that is visible by default (multi-series is rendered
    as overlapping thin lines; bindings with `visible_by_default: false` are left out, as the
    full chart hides them until the legend switches them on — all bindings are drawn when
    none is visible by default),
  - colours follow `series_bindings[].color` (incl. `token:` CSS-tokens), like the full chart,
  - the backend's default date range for that viz,
  - the backend's default filter values.

  Then we throw away the heavy chrome — axes, gridlines, legends,
  tooltips, header, footer, filter dropdowns — and render the resulting
  series as a multi-series Highcharts sparkline. The card surfaces:
    - the user-facing chart label (passed in as `title`),
    - the multi-line sparkline,
    - a one-line footer with either the dominant series' latest value +
      delta OR min/max over the rendered window (configurable per viz),
    - a → link to the full dataset/visualization page.

  Per-visualization rendering hints come from the YAML `mini_view` block
  on the visualization config. The backend echoes that block as
  `response.mini_view`. Supported fields:
    - `line_style`: 'line' (default) | 'step' | 'spline' | 'area'
    - `footer`:     'latest' (default) | 'min_max' | 'none'
    - `period`:     date-range override (e.g. 'last_7d'); when omitted
                    we honour the viz's own `time_controls.date_range.default`
                    so daily/weekly datasets don't get a 24h window that
                    would yield 0–1 points.
-->
<template>
  <div ref="rootEl" class="mini-graph min-w-0">
    <header class="flex items-baseline justify-between gap-2 min-w-0 mb-1.5">
      <h4 class="text-sm font-medium text-theme-text truncate">
        {{ effectiveTitle }}
      </h4>
      <!-- Decorative arrow only — the parent card owns the actual link,
           and nesting a `<router-link>` inside another would produce
           invalid nested `<a>` tags. Kept as a visual affordance so users
           still see "this card is clickable". -->
      <span
        v-if="deepLink"
        class="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-md
          text-theme-palette-emerald dark:text-theme-palette-emerald"
        :aria-hidden="true"
        :title="t('mijn_omgeving.charts.open_full')"
      >
        <icon :path="mdiArrowRight" size="14" />
      </span>
    </header>

    <div :style="{ height: `${height}px` }" class="relative overflow-hidden">
      <!-- Loading state — slim skeleton stripe matched to sparkline height. -->
      <div
        v-if="loading"
        class="absolute inset-0 rounded
          bg-theme-bg-subtle dark:bg-theme-surface-elevated animate-pulse"
        aria-hidden="true"
      />

      <!-- Locked state — subscription doesn't include this dataset. A compact
           lock + "Requires <plan>" that deep-links into the upgrade flow,
           matching the bigger restricted state on full charts/KPIs. -->
      <button
        v-else-if="accessDenied"
        type="button"
        data-test="restricted-access"
        :data-entitlement-key="accessDetail.entitlementKey"
        :title="requiresLabel"
        class="absolute inset-0 flex flex-col items-center justify-center gap-1 rounded
          text-theme-palette-amber transition hover:bg-theme-palette-amber/5
          dark:text-theme-palette-amber"
        @click="onUpgrade"
      >
        <icon :path="mdiLockOutline" size="18" />
        <span class="max-w-full truncate px-2 text-[11px] font-medium leading-tight">
          {{ requiresLabel }}
        </span>
      </button>

      <!-- Empty / error state — keep it minimal; the deep link still lets the
           user jump to the full chart for diagnosis. -->
      <p
        v-else-if="error || !hasAnyData"
        class="absolute inset-0 flex items-center justify-center text-[11px] text-theme-text-muted"
      >
        {{ error || t('mijn_omgeving.charts.no_data') }}
      </p>

      <div v-else ref="chartEl" class="mini-graph__canvas" aria-hidden="true" />
    </div>

    <!-- Per-series footer. The footer KIND is driven by the viz's
         `mini_view.footer` config:
           - 'latest'  (default) → "LAATSTE" header + `● label  value  Δ%`,
                                    one row per series.
           - 'min_max'           → "MIN / MAX" header + `● label  min  max`,
                                    handy for charts where the *level*
                                    (DA prices, capacity prices) matters
                                    more than the trend.
           - 'none'              → footer hidden entirely. -->
    <footer
      v-if="!loading && !error && hasAnyData && footerKind !== 'none'"
      class="mt-1.5 space-y-0.5 text-[11px] tabular-nums"
    >
      <!-- Header columns align right so the MIN / MAX (or LAATSTE) labels
           sit directly above their value columns. When every series in
           the footer shares one unit (the common case — DA prices in
           €/MWh, TB spreads in €/MW), we hoist the unit into this header
           ("MIN / MAX in €/MW") so the value rows can show bare numbers,
           buying horizontal room and avoiding the unit being repeated on
           every row. Mixed-unit charts (rare) fall back to per-row units. -->
      <div
        class="flex items-baseline gap-2 text-[10px] uppercase tracking-wide text-theme-text-muted"
      >
        <span class="flex-1 min-w-0" aria-hidden="true" />
        <template v-if="footerKind === 'min_max'">
          <span class="shrink-0 w-14 text-right">{{ t('mijn_omgeving.charts.footer_min') }}</span>
          <span class="shrink-0 w-20 text-right">
            {{ t('mijn_omgeving.charts.footer_max') }}<template v-if="sharedUnit">
              <span class="ml-1 normal-case tracking-normal text-theme-text-secondary">
                {{ t('mijn_omgeving.charts.footer_in') }} {{ sharedUnit }}
              </span>
            </template>
          </span>
        </template>
        <template v-else>
          <span class="shrink-0 text-right">
            {{ t('mijn_omgeving.kpis.latest') }}<template v-if="sharedUnit">
              <span class="ml-1 normal-case tracking-normal text-theme-text-secondary">
                {{ t('mijn_omgeving.charts.footer_in') }} {{ sharedUnit }}
              </span>
            </template>
          </span>
        </template>
      </div>
      <ul class="space-y-0.5">
        <li
          v-for="row in seriesFooterRows"
          :key="row.id"
          class="flex items-baseline gap-2 min-w-0 whitespace-nowrap"
        >
          <span class="flex items-baseline gap-1.5 min-w-0 flex-1">
            <span
              class="shrink-0 inline-block w-1.5 h-1.5 rounded-full"
              :style="{ backgroundColor: row.color }"
              aria-hidden="true"
            />
            <span class="truncate text-theme-text-secondary">
              {{ row.label }}
            </span>
          </span>
          <template v-if="footerKind === 'min_max'">
            <!-- When every series shares one unit it's hoisted into the
                 header ("MIN / MAX in €/MW"), so both columns render bare
                 numbers. Mixed-unit charts keep the unit on the MAX cell
                 (the bigger value) as a per-row fallback. -->
            <span class="shrink-0 text-theme-text-secondary w-14 text-right">
              {{ formatNumber(row.minValue) }}
            </span>
            <span class="shrink-0 text-theme-text font-semibold w-20 text-right">
              {{ sharedUnit ? formatNumber(row.maxValue) : formatValue(row.maxValue, row.unit) }}
            </span>
          </template>
          <template v-else>
            <span class="shrink-0 text-theme-text font-semibold text-right">
              {{ sharedUnit ? formatNumber(row.lastValue) : formatValue(row.lastValue, row.unit) }}
            </span>
            <span
              v-if="Number.isFinite(row.trendPct)"
              :class="[
                'shrink-0 w-12 text-right',
                row.trendPct > 0
                  ? 'text-theme-palette-rose dark:text-theme-palette-rose'
                  : row.trendPct < 0
                    ? 'text-theme-palette-emerald dark:text-theme-palette-emerald'
                    : 'text-theme-text-muted',
              ]"
            >
              {{ formatTrendLabel(row.trendPct) }}
            </span>
          </template>
        </li>
      </ul>
    </footer>
  </div>
</template>

<script setup>
  /* eslint-disable no-undef */
  import Icon from '@/components/Icon';
  import {
    SERIES_COLORS,
    resolveBindingColor,
  } from '@/components/visualizations/graph/utils/chartColors';
  import { useChromeStore } from '@/stores/useChromeStore';
  import { useGrantedGetVisualization } from '@/composables/charts/useVisualizationGrant';
  import { isAccessDeniedError, extractAccessDeniedDetail } from '@/utils/accessDenied';
  import { goToUpgrade } from '@/utils/upgradeNavigation';
  import { mdiArrowRight, mdiLockOutline } from '@mdi/js';
  import Highcharts from 'highcharts/highstock';
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  // A chart on a public newsroom article sends its visualization grant (see the composable).
  const getVisualization = useGrantedGetVisualization();

  const props = defineProps({
    datasetId: { type: String, required: true },
    visualizationId: { type: String, required: true },
    title: { type: String, default: '' },
    /** Optional deep link rendered as a small "→" in the title row. */
    deepLink: { type: String, default: '' },
    /** Sparkline height in pixels. Defaults to a tight snapshot height. */
    height: { type: Number, default: 56 },
    /**
     * Date range applied to the backend request. When `null` (default)
     * we honour the visualization's configured `time_controls.date_range
     * .default` — that's almost always what the user wants for a
     * sparkline, because daily/weekly datasets would otherwise yield
     * 0–1 points under a hardcoded short window. The viz config can
     * still narrow this via its own `mini_view.period` block.
     */
    period: { type: String, default: null },
  });

  const { t, locale } = useI18n();

  const loading = ref(true);
  const error = ref('');
  const accessDenied = ref(false);
  const accessDetail = ref({ entitlementKey: '', offer: null });
  const rootEl = ref(null);
  const chartEl = ref(null);
  let chartInstance = null;
  let resizeObserver = null;

  // Resolved viz config + data.
  const seriesBindings = ref([]); // from response.series_bindings
  const seriesData = ref([]); // from response.data — same order as series_bindings
  // Per-viz rendering hints from the YAML `mini_view` block, echoed by the
  // backend. `null` means "use built-in defaults".
  const miniView = ref(null);

  const VALID_LINE_STYLES = ['line', 'step', 'spline', 'area'];
  const VALID_FOOTER_KINDS = ['latest', 'min_max', 'none'];

  const lineStyle = computed(() => {
    const declared = miniView.value?.line_style;
    return VALID_LINE_STYLES.includes(declared) ? declared : 'line';
  });
  const footerKind = computed(() => {
    const declared = miniView.value?.footer;
    return VALID_FOOTER_KINDS.includes(declared) ? declared : 'latest';
  });
  // Title override: prefer the per-viz `mini_view.title` (e.g. "TB Spread
  // Last 7 Days") over whatever the parent card passes in. Falls through
  // to the parent's `title` prop so favourites without a mini-specific
  // label keep their existing label.
  const effectiveTitle = computed(() => miniView.value?.title || props.title || '');

  // Locked-state label: prefer the resolved plan name from the 403 offer
  // ("Vereist Flex"), otherwise the generic upgrade CTA.
  const requiresLabel = computed(() => {
    const planName = accessDetail.value?.offer?.name;
    return planName
      ? t('dataset.restricted.requires', { plan: planName })
      : t('dataset.restricted.cta');
  });

  function onUpgrade() {
    goToUpgrade(accessDetail.value.entitlementKey || null, accessDetail.value.offer);
  }

  // Fallback palette in case `series_bindings[].color` is missing. Shared
  // with the full DatasetChart via `SERIES_COLORS` so the same series
  // lands on the same colour in both renderers — Highcharts assigns
  // colours for line/area charts from this same array internally, so
  // picking by index here matches what the full chart shows.
  const FALLBACK_PALETTE = SERIES_COLORS;

  // Token resolution and binding-colour precedence as in the full chart (seriesBuilder.js):
  // a catalog `token:` colour resolves to the active theme's concrete rgb, so the card shows
  // the dashboard's colour (e.g. the TB ramp) instead of a raw `token:` string.
  function _seriesColor(binding, seriesObj, index) {
    return (
      resolveBindingColor(binding?.color || binding?.style_overrides?.color) ||
      seriesObj?.color ||
      FALLBACK_PALETTE[index % FALLBACK_PALETTE.length]
    );
  }

  function _toMs(t) {
    if (t == null) return null;
    if (typeof t === 'number') return t > 1e12 ? t : t * 1000;
    const ms = new Date(t).getTime();
    return Number.isNaN(ms) ? null : ms;
  }

  // Forward-market style datasets (ICE Power Base, TTF Cals, ...) declare
  // `groups: ['Cal 27', 'Cal 28', ...]` on the series response and each
  // point carries `value: list[float|null]` (vector positions map to
  // groups). The plain sparkline can't draw a vector, so we flatten the
  // grouped series into one scalar sub-series per group BEFORE any
  // normalization happens. Scalar series pass through untouched.
  function _expandGroupedSeries(rawSeries) {
    const out = [];
    for (const s of rawSeries || []) {
      const groups = Array.isArray(s?.groups) ? s.groups : null;
      const points = s?.data || s?.points || [];
      const looksVector =
        groups && groups.length > 0 && points.some((p) => Array.isArray(p?.value ?? p?.v ?? p?.y));
      if (!looksVector) {
        out.push(s);
        continue;
      }
      groups.forEach((groupLabel, groupIdx) => {
        const subPoints = points.map((p) => {
          const vec = p?.value ?? p?.v ?? p?.y;
          const value = Array.isArray(vec) ? vec[groupIdx] : null;
          return { timestamp: p?.timestamp ?? p?.time ?? p?.t ?? p?.x, value };
        });
        out.push({
          ...s,
          id: `${s?.id || s?.series_id || 'series'}::${groupLabel}`,
          series_id: undefined,
          title: groupLabel,
          label: groupLabel,
          groups: null,
          data: subPoints,
        });
      });
    }
    return out;
  }

  function _normalizePoints(rawPoints) {
    const out = [];
    for (const p of rawPoints || []) {
      let t = null;
      let v = null;
      if (Array.isArray(p)) {
        t = _toMs(p[0]);
        v = Number(p[1]);
      } else if (p && typeof p === 'object') {
        t = _toMs(p.timestamp ?? p.time ?? p.t ?? p.x);
        v = Number(p.value ?? p.v ?? p.y);
      }
      if (t === null || !Number.isFinite(v)) continue;
      out.push([t, v]);
    }
    // Highcharts requires ascending timestamps.
    out.sort((a, b) => a[0] - b[0]);
    return out;
  }

  async function load() {
    loading.value = true;
    error.value = '';
    accessDenied.value = false;
    accessDetail.value = { entitlementKey: '', offer: null };
    // Round 1: fetch the viz config (no data) to discover `mini_view.period`
    // and the configured default date range. We need this BEFORE the data
    // round so the backend gets the right window — otherwise daily-aggregated
    // visualizations (like TB-spreads or aFRR capacity prices) end up with
    // an arbitrary 24h window that yields 0–1 points.
    let configPeriod = null;
    try {
      const configResponse = await getVisualization(
        props.datasetId,
        props.visualizationId,
        { includeData: false },
      );
      miniView.value = configResponse?.mini_view || null;
      // Priority order: explicit mini override (structured `date_range.default`
      // wins over the deprecated `period` shorthand) → the viz's own
      // configured default → null (which means we'll fall back to a 30d
      // safety net below).
      configPeriod =
        miniView.value?.date_range?.default ||
        miniView.value?.period ||
        configResponse?.time_controls?.date_range?.default ||
        null;
    } catch (e) {
      // Fall through — the data round below will retry the request and the
      // error path there will surface "Geen data" (or the locked state) to
      // the user. Capture a 403 here too so a config-only failure still locks.
      miniView.value = null;
      if (isAccessDeniedError(e)) {
        accessDenied.value = true;
        accessDetail.value = extractAccessDeniedDetail(e);
      }
    }

    const resolvedPeriod = props.period || configPeriod || 'last_30d';
    try {
      const response = await getVisualization(props.datasetId, props.visualizationId, {
        includeData: true,
        period: resolvedPeriod,
      });
      // Mini-view may also live on the data response — prefer the data
      // response if both rounds returned it (keeps a single source of truth).
      if (response?.mini_view) miniView.value = response.mini_view;
      seriesBindings.value = response?.series_bindings || [];
      // Response data is an array of series objects matching the bindings.
      // Each series carries `id` (or `series_id`) + `data` (point array). We
      // match by id so colors line up even if order drifts. Grouped series
      // (`groups: ['Cal 27', ...]`, value=list) are expanded into one
      // scalar sub-series per group label so the sparkline can render them.
      seriesData.value = _expandGroupedSeries(response?.data || response?.series || []);
    } catch (e) {
      if (isAccessDeniedError(e)) {
        accessDenied.value = true;
        accessDetail.value = extractAccessDeniedDetail(e);
      } else {
        error.value = t('mijn_omgeving.charts.no_data');
      }
      seriesBindings.value = [];
      seriesData.value = [];
    } finally {
      loading.value = false;
      // Wait for Vue to swap the `v-if="loading"` skeleton for the chart
      // container (`<div ref="chartEl">`) before mounting Highcharts —
      // otherwise `chartEl.value` is still null on the same tick and the
      // canvas ends up empty (footer renders, chart doesn't).
      await nextTick();
      renderChart();
    }
  }

  function _bindingFor(seriesObj, index) {
    const sid = seriesObj?.series_id || seriesObj?.id;
    const byId = sid && seriesBindings.value.find((b) => b.id === sid || b.series_id === sid);
    return byId || seriesBindings.value[index] || null;
  }

  // The series the card draws: the chart's DEFAULT selection, not every binding. A binding
  // with `visible_by_default: false` (TB3 and TB5–TB12 on the TB-spreads charts, PI-255) is
  // hidden in the full chart until the user switches it on in the legend; the card has no
  // legend, so it leaves those out. A visualization that hides every binding still draws all
  // of them rather than an empty card. `index` stays the position in the response so the
  // fallback palette keeps assigning the same colour to the same series.
  const renderedSeries = computed(() => {
    const all = seriesData.value.map((seriesObj, index) => ({
      seriesObj,
      index,
      binding: _bindingFor(seriesObj, index),
    }));
    const visible = all.filter(({ binding }) => binding?.visible_by_default !== false);
    return visible.length ? visible : all;
  });

  function _buildHighchartsSeries() {
    const style = lineStyle.value;
    const result = [];
    renderedSeries.value.forEach(({ seriesObj, index, binding }) => {
      const data = _normalizePoints(seriesObj?.data || seriesObj?.points || []);
      if (!data.length) return;
      // Only the first drawn binding gets the soft fill. Counted after empty series are
      // skipped, so an empty first binding (e.g. a prediction chart's `actual` for
      // tomorrow) passes the fill on to the next drawn series.
      const isFirst = result.length === 0;
      const color = _seriesColor(binding, seriesObj, index);

      // Pick the Highcharts series type from the viz's `mini_view.line_style`:
      //   - line   → plain line; first series fills as soft area for shape.
      //   - step   → left-step line; matches DA-price/auction shape exactly.
      //   - spline → smoothed; first series fills as soft areaspline.
      //   - area   → filled area on every series (rare; used for SoC-style).
      // Only the first series gets a fill in line/spline so multi-line charts
      // stay readable; step charts skip the fill so the rises are clean.
      let entry;
      if (style === 'step') {
        entry = { type: 'line', step: 'left', data, color, lineWidth: 1.5 };
      } else if (style === 'spline') {
        entry = isFirst
          ? { type: 'areaspline', data, color, lineWidth: 1.5, fillColor: _toRgba(color, 0.18) }
          : { type: 'spline', data, color, lineWidth: 1.5 };
      } else if (style === 'area') {
        entry = {
          type: 'area',
          data,
          color,
          lineWidth: 1.5,
          fillColor: _toRgba(color, 0.18),
        };
      } else {
        // 'line' (default)
        entry = isFirst
          ? { type: 'areaspline', data, color, lineWidth: 1.5, fillColor: _toRgba(color, 0.18) }
          : { type: 'line', data, color, lineWidth: 1.5 };
      }
      result.push(entry);
    });
    return result;
  }

  function _toRgba(color, alpha) {
    // Resolved theme colours arrive as `rgb(r, g, b)`; keep their hue for the soft fill.
    const rgb =
      typeof color === 'string' && color.match(/^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})/i);
    if (rgb) return `rgb(${rgb[1]} ${rgb[2]} ${rgb[3]} / ${alpha})`;
    if (!color || typeof color !== 'string' || !color.startsWith('#')) {
      return `rgb(var(--color-brand-primary) / ${alpha})`;
    }
    const clean = color.replace('#', '');
    const expanded =
      clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
    const r = parseInt(expanded.slice(0, 2), 16);
    const g = parseInt(expanded.slice(2, 4), 16);
    const b = parseInt(expanded.slice(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return `rgb(var(--color-brand-primary) / ${alpha})`;
    return `rgb(${r} ${g} ${b} / ${alpha})`;
  }

  async function renderChart() {
    if (chartInstance) {
      chartInstance.destroy();
      chartInstance = null;
    }
    // Mount target may not exist yet on the very first render (the
    // `v-if="loading"`/`v-else-if="error"` blocks share their slot with the
    // chart canvas). Yield one frame and retry once so we don't silently no-op
    // on transitions between states.
    if (!chartEl.value) {
      await nextTick();
      if (!chartEl.value) return;
    }
    const series = _buildHighchartsSeries();
    if (!series.length) return;

    chartInstance = Highcharts.chart(chartEl.value, {
      chart: {
        type: 'line',
        backgroundColor: 'transparent',
        margin: [2, 0, 2, 0],
        spacing: [0, 0, 0, 0],
        // Pin the width to the measured container so Highcharts never falls
        // back to its 600px default when the container can't be measured at
        // mount (which would overflow the snapshot card). The ResizeObserver
        // below keeps it in sync as the grid column resizes.
        width: chartEl.value.offsetWidth || null,
        height: props.height,
        animation: false,
      },
      title: { text: null },
      credits: { enabled: false },
      legend: { enabled: false },
      tooltip: { enabled: false },
      xAxis: {
        visible: false,
        type: 'datetime',
        startOnTick: false,
        endOnTick: false,
        minPadding: 0,
        maxPadding: 0,
      },
      yAxis: {
        visible: false,
        startOnTick: false,
        endOnTick: false,
        minPadding: 0,
        maxPadding: 0,
      },
      plotOptions: {
        series: {
          marker: { enabled: false },
          states: { hover: { enabled: false }, inactive: { opacity: 1 } },
          enableMouseTracking: false,
          animation: false,
        },
      },
      series,
    });
  }

  // Highcharts only auto-resizes on window resize, not when its own container
  // shrinks/grows (e.g. the snapshot grid column reflowing). Without this the
  // sparkline keeps the width it was first rendered at and overflows its card.
  // Mirrors the full GraphVisualization's useChartResize behaviour, kept inline
  // here because the mini chart has none of that composable's other machinery.
  function setupResizeObserver() {
    if (resizeObserver || typeof ResizeObserver === 'undefined' || !rootEl.value) return;
    resizeObserver = new ResizeObserver(() => {
      if (!chartInstance || !chartEl.value) return;
      const width = chartEl.value.offsetWidth;
      if (width > 0) chartInstance.setSize(width, props.height, false);
    });
    resizeObserver.observe(rootEl.value);
  }

  onMounted(() => {
    setupResizeObserver();
    load();
  });
  onBeforeUnmount(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    if (chartInstance) {
      chartInstance.destroy();
      chartInstance = null;
    }
  });
  watch(() => [props.datasetId, props.visualizationId, props.period], load);
  watch(() => props.height, renderChart);

  const chromeStore = useChromeStore();
  // Token colours are resolved to concrete rgb at render time, so a theme flip needs a
  // redraw (GraphVisualization does the same); reassigning seriesData refreshes the footer swatches.
  watch(
    () => chromeStore.darkMode,
    async () => {
      if (loading.value || !seriesData.value.length) return;
      seriesData.value = [...seriesData.value];
      await nextTick();
      renderChart();
    }
  );

  // ── Per-series footer rows ───────────────────────────────────────────────
  //
  // The footer renders one line per rendered series (`renderedSeries`), mirroring the chart's
  // line colors so the user can read each line at a glance. Each row carries
  // the binding's human label, the LATEST data point's value (with the
  // series' configured unit), and the delta over the visualization's
  // configured time window. Capped at 3 rows so multi-binding vizs don't
  // overrun the snapshot card.

  const MAX_FOOTER_ROWS = 3;

  const seriesFooterRows = computed(() => {
    const rows = [];
    renderedSeries.value.forEach(({ seriesObj, index, binding }) => {
      const pts = _normalizePoints(seriesObj?.data || seriesObj?.points || []);
      if (!pts.length) return;
      const last = pts[pts.length - 1][1];
      const first = pts[0][1];
      const trendPct = first === 0 || first == null ? null : ((last - first) / Math.abs(first)) * 100;
      let minValue = pts[0][1];
      let maxValue = pts[0][1];
      for (let i = 1; i < pts.length; i += 1) {
        const v = pts[i][1];
        if (v < minValue) minValue = v;
        if (v > maxValue) maxValue = v;
      }
      const color = _seriesColor(binding, seriesObj, index);
      const label =
        binding?.label ||
        seriesObj?.label ||
        binding?.title ||
        seriesObj?.title ||
        binding?.id ||
        seriesObj?.series_id ||
        seriesObj?.id ||
        `Serie ${index + 1}`;
      const unit = binding?.unit || seriesObj?.unit || '';
      rows.push({
        id: binding?.id || seriesObj?.series_id || seriesObj?.id || `s-${index}`,
        label,
        color,
        unit,
        lastValue: last,
        trendPct,
        minValue,
        maxValue,
      });
    });
    return rows.slice(0, MAX_FOOTER_ROWS);
  });

  // Hoist the unit into the footer header label when every series shares
  // the same one — saves a lot of horizontal room on narrow cards and
  // mirrors how a normal axis would label "values in €/MW". Returns null
  // when units differ across series or are missing on any row, so the
  // per-row unit fallback can kick in.
  const sharedUnit = computed(() => {
    const rows = seriesFooterRows.value;
    if (!rows.length) return null;
    const first = rows[0].unit;
    if (!first) return null;
    for (let i = 1; i < rows.length; i += 1) {
      if (rows[i].unit !== first) return null;
    }
    return first;
  });

  // `hasAnyData` reflects RENDERABLE data, not raw point count. When a viz
  // returns rows whose `value` field is null/undefined/a vector (e.g. the
  // daily-aggregate boundary or a grouped series), the raw arrays are
  // non-empty but `_normalizePoints` strips everything — checking raw
  // length there would let the footer mount with no rows and the chart
  // mount with no series ("LAATSTE" header above a blank card). Computing
  // against normalized data keeps the empty-state copy consistent.
  const hasAnyData = computed(() =>
    renderedSeries.value.some(
      ({ seriesObj: s }) => _normalizePoints(s?.data || s?.points || []).length > 0,
    ),
  );

  function formatValue(v, unit) {
    if (v == null || !Number.isFinite(v)) return '—';
    const formatted = v.toLocaleString(locale.value, { maximumFractionDigits: 2 });
    if (!unit) return formatted;
    if (unit === '€' || unit === 'EUR') return `€ ${formatted}`;
    return `${formatted} ${unit}`;
  }

  // Bare-number formatter for the MIN column in min/max footers — the unit
  // is shown once on the MAX value, so repeating it here would cause long
  // strings ("1.870,62 €/MW") to overflow the narrow card and wrap onto
  // a second line.
  function formatNumber(v) {
    if (v == null || !Number.isFinite(v)) return '—';
    return v.toLocaleString(locale.value, { maximumFractionDigits: 2 });
  }

  function formatTrendLabel(pct) {
    if (pct == null || !Number.isFinite(pct)) return '';
    const sign = pct > 0 ? '+' : '';
    return `${sign}${pct.toFixed(1)}%`;
  }
</script>

<style scoped>
  .mini-graph__canvas {
    width: 100%;
    height: 100%;
  }
</style>
